(() => {
  if (customElements.get("iv-model-file")) return;
  class ModelFile extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const on = (el, event, fn) =>
        el.addEventListener(event, fn, { signal: this.abort.signal });
      this.nav = this.querySelector(".iv-nav");
      this.links = [...this.nav.querySelectorAll("a")];
      this.parts = [...this.querySelectorAll("[data-iv-scroll]")];
      this.bottom = this.querySelector(".iv-bottom");
      this.dialog = this.querySelector("dialog");
      this.tabs = [...this.querySelectorAll("[role=tab]")];
      this.frame = 0;
      this.reviewInterval = null;
      this.schedule = () => {
        if (!this.frame)
          this.frame = requestAnimationFrame(() => {
            this.frame = 0;
            this.update();
          });
      };
      on(window, "scroll", this.schedule);
      on(window, "resize", this.schedule);
      this.resize = new ResizeObserver(this.schedule);
      this.resize.observe(this.nav);
      this.mutations = new MutationObserver(this.schedule);
      this.mutations.observe(document.body, { childList: true, subtree: true });
      this.querySelectorAll("[data-iv-zoom]").forEach((button) =>
        on(button, "click", () => {
          this.opener = button;
          const img = this.dialog.querySelector("img");
          img.src = button.dataset.ivZoom;
          img.alt = button.dataset.ivAlt;
          this.dialog.querySelector(".iv-dialog-caption").textContent =
            button.dataset.ivAlt;
          this.dialog.showModal();
          this.dialog.querySelector("[data-iv-close]").focus();
          this.schedule();
        }),
      );
      on(this.dialog.querySelector("[data-iv-close]"), "click", () =>
        this.dialog.close(),
      );
      on(this.dialog, "close", () => {
        this.opener?.focus();
        this.schedule();
      });
      on(this.dialog, "keydown", (event) => {
        if (event.key !== "Tab") return;
        const controls = [
          ...this.dialog.querySelectorAll(
            "button,a[href],input,select,textarea,[tabindex]",
          ),
        ].filter((el) => !el.disabled && el.tabIndex >= 0 && !el.hidden);
        const first = controls[0],
          last = controls.at(-1);
        if (!first) {
          event.preventDefault();
          return;
        }
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
      on(this.dialog, "click", (event) => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (
          event.clientX < rect.left ||
          event.clientX > rect.right ||
          event.clientY < rect.top ||
          event.clientY > rect.bottom
        )
          this.dialog.close();
      });
      this.tabs.forEach((tab, index) => {
        on(tab, "click", () => this.selectTab(index));
        on(tab, "keydown", (event) => {
          let next = index;
          if (event.key === "ArrowRight") next = (index + 1) % this.tabs.length;
          else if (event.key === "ArrowLeft")
            next = (index + this.tabs.length - 1) % this.tabs.length;
          else if (event.key === "Home") next = 0;
          else if (event.key === "End") next = this.tabs.length - 1;
          else return;
          event.preventDefault();
          this.selectTab(next);
          this.tabs[next].focus();
        });
      });
      this.querySelectorAll("[data-iv-jump]").forEach((button) =>
        on(button, "click", () => {
          const target = this.querySelector(
            "#" + CSS.escape(button.dataset.ivJump),
          );
          target?.scrollIntoView({
            behavior: matchMedia("(prefers-reduced-motion: reduce)").matches
              ? "auto"
              : "smooth",
            block: "start",
          });
        }),
      );
      this.querySelectorAll("[data-iv-open-shop-reviews]").forEach((button) =>
        on(button, "click", () => this.openShopReviews(button)),
      );
      this.update();
    }
    selectTab(index) {
      this.tabs.forEach((tab, i) => {
        const selected = i === index;
        tab.setAttribute("aria-selected", String(selected));
        tab.tabIndex = selected ? 0 : -1;
        this.querySelector(
          "#" + CSS.escape(tab.getAttribute("aria-controls")),
        ).hidden = !selected;
      });
    }
    openShopReviews(button) {
      if (this.openingReviews) return;
      this.openingReviews = true;

      const feedback = this.querySelector(".iv-shop-reviews-feedback");
      if (feedback) feedback.hidden = true;

      const isVisible = (el) => {
        if (!el) return false;
        const rect = el.getBoundingClientRect();
        return (
          el.getClientRects().length > 0 &&
          rect.width > 0 &&
          rect.height > 0 &&
          getComputedStyle(el).visibility !== "hidden" &&
          getComputedStyle(el).display !== "none"
        );
      };

      const getSubtab = () =>
        document.querySelector(
          'span.jdgm-subtab__name[data-tabname="shop-reviews"], .jdgm-subtab__name[data-tabname="shop-reviews"], [data-tabname="shop-reviews"]'
        );

      // Case: Already visible in an open modal
      const existingSubtab = getSubtab();
      if (isVisible(existingSubtab)) {
        existingSubtab.click();
        this.openingReviews = false;
        return;
      }

      // If not yet visible, trigger the native opener and wait for visibility
      let openerClicked = false;
      const start = Date.now();

      if (this.reviewInterval) clearInterval(this.reviewInterval);
      this.reviewInterval = setInterval(() => {
        if (!this.isConnected) {
          clearInterval(this.reviewInterval);
          this.openingReviews = false;
          return;
        }

        // Attempt to click opener if not done yet
        if (!openerClicked) {
          const opener = document.querySelector(
            'div.jdgm-revs-tab-btn.btn, .jdgm-revs-tab-btn, div[role="button"].jdgm-revs-tab-btn'
          );
          if (opener && isVisible(opener)) {
            opener.click();
            openerClicked = true;
          }
        }

        // Check if subtab has become visible
        const subtab = getSubtab();
        if (isVisible(subtab)) {
          subtab.click();
          clearInterval(this.reviewInterval);
          this.reviewInterval = null;
          this.openingReviews = false;
          return;
        }

        // Timeout after 3 seconds
        if (Date.now() - start > 3000) {
          clearInterval(this.reviewInterval);
          this.reviewInterval = null;
          this.openingReviews = false;
          if (feedback) feedback.hidden = false;
        }
      }, 60);
    }
    update() {
      let offset = 0;
      try {
        document.querySelectorAll(this.dataset.headerSelector).forEach((el) => {
          const pos = getComputedStyle(el).position;
          const rect = el.getBoundingClientRect();
          if (
            (pos === "fixed" || pos === "sticky") &&
            rect.bottom > 0 &&
            rect.height < innerHeight / 2
          )
            offset = Math.max(offset, rect.bottom);
        });
      } catch {}
      this.style.setProperty("--iv-header-offset", offset + "px");
      const navHeight = this.nav.getBoundingClientRect().height;
      this.style.setProperty("--iv-nav-height", navHeight + "px");
      let active = this.parts[0];
      this.parts.forEach((part) => {
        if (part.getBoundingClientRect().top <= offset + navHeight + 45)
          active = part;
      });
      this.links.forEach((link) => {
        const selected = link.hash === "#" + active.id;
        link.classList.toggle("active", selected);
        if (selected) link.setAttribute("aria-current", "location");
        else link.removeAttribute("aria-current");
      });
      let widgetOffset = 12;
      document
        .querySelectorAll("#tidio-chat-root,.tidio-chat-root")
        .forEach((el) => {
          const r = el.getBoundingClientRect();
          if (
            r.width > 0 &&
            r.width < 400 &&
            r.height > 0 &&
            r.height < 400 &&
            r.top < innerHeight &&
            r.bottom > innerHeight - 180
          )
            widgetOffset = Math.max(widgetOffset, innerHeight - r.top + 12);
        });
      this.style.setProperty("--iv-widget-offset", widgetOffset + "px");
      const actions = [...this.querySelectorAll("[data-iv-action]")];
      const visible = actions.some((el) => {
        const r = el.getBoundingClientRect();
        return (
          r.bottom > offset + navHeight &&
          r.top < innerHeight - widgetOffset - 80
        );
      });
      this.bottom.hidden = innerWidth > 760 || visible || this.dialog.open;
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.resize?.disconnect();
      this.mutations?.disconnect();
      if (this.reviewInterval) clearInterval(this.reviewInterval);
      cancelAnimationFrame(this.frame);
      if (this.dialog?.open) this.dialog.close();
    }
  }
  customElements.define("iv-model-file", ModelFile);
})();
